import React, { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import api from '../services/api';

const API_KEY_STORAGE_KEY = 'externalIntegration_apiKey';

const getErrorMessage = (error) => {
  const status = error?.response?.status;
  if (status === 401) return 'Integration not authorized. Please confirm your API key.';
  if (status === 404) return 'Question not found.';
  if (status === 500) return 'Server error. Please try again shortly.';
  return error?.response?.data?.error || error?.message || 'Unable to load bot context.';
};

const ExternalQuestionBotPage = () => {
  const { questionId } = useParams();
  const [apiKeyInput, setApiKeyInput] = useState(() => localStorage.getItem(API_KEY_STORAGE_KEY) || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [payload, setPayload] = useState(null);

  const canLoad = useMemo(() => Boolean(questionId && apiKeyInput.trim()), [questionId, apiKeyInput]);

  const fetchBotContext = async () => {
    if (!canLoad) return;

    setLoading(true);
    setError('');

    try {
      localStorage.setItem(API_KEY_STORAGE_KEY, apiKeyInput.trim());
      const response = await api.get(`/public/bot/${questionId}`, {
        headers: {
          'x-api-key': apiKeyInput.trim()
        }
      });
      setPayload(response.data || null);
    } catch (err) {
      setPayload(null);
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <h1 className="text-2xl font-bold text-gray-900">Question Details + Bot Explanation</h1>
          <p className="text-sm text-gray-600 mt-1">Open a question context with explanation and similar recommendations.</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-5">
            <div>
              <label htmlFor="external-question-id" className="block text-sm font-medium text-gray-700 mb-1">Question ID</label>
              <input
                id="external-question-id"
                value={questionId || ''}
                readOnly
                className="w-full px-3 py-2 rounded-lg border border-gray-300 bg-gray-50"
              />
            </div>
            <div>
              <label htmlFor="external-question-api-key" className="block text-sm font-medium text-gray-700 mb-1">API Key</label>
              <input
                id="external-question-api-key"
                value={apiKeyInput}
                onChange={(e) => setApiKeyInput(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter external integration API key"
                type="password"
              />
            </div>
          </div>

          <div className="mt-4 flex items-center gap-3">
            <button
              onClick={fetchBotContext}
              disabled={!canLoad || loading}
              className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white px-4 py-2 rounded-lg text-sm font-medium"
            >
              {loading ? 'Loading...' : 'Load Bot Context'}
            </button>
            <Link to="/external/courses/1/lessons" className="text-sm text-blue-700 hover:text-blue-900">
              ← Back to course lessons
            </Link>
          </div>

          {error && (
            <div className="mt-4 bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">
              {error}
            </div>
          )}
        </div>

        {payload && (
          <div className="space-y-4">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
              <p className="text-xs text-gray-500 uppercase tracking-wide">Question</p>
              <p className="text-gray-900 font-medium mt-2">{payload.question?.text || 'No question text available.'}</p>

              {Array.isArray(payload.question?.options) && payload.question.options.length > 0 && (
                <ul className="mt-4 space-y-2">
                  {payload.question.options.map((option, index) => (
                    <li key={`${index}-${option}`} className="text-sm text-gray-700 bg-gray-50 px-3 py-2 rounded-lg border border-gray-100">
                      {option}
                    </li>
                  ))}
                </ul>
              )}

              {payload.question?.answer && (
                <p className="mt-3 text-sm text-green-700 font-medium">Answer: {payload.question.answer}</p>
              )}
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
              <div className="flex items-center justify-between gap-3">
                <h2 className="text-lg font-semibold text-gray-900">Explanation</h2>
                <span className="text-xs px-2.5 py-1 rounded-full bg-indigo-100 text-indigo-700 font-medium">
                  Source: {payload.explanation?.source || 'unknown'}
                </span>
              </div>
              <p className="text-sm text-gray-700 mt-3 leading-6">{payload.explanation?.text || 'No explanation available.'}</p>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                <h3 className="font-semibold text-gray-900">Similar Questions</h3>
                <span className="text-sm text-gray-500">{payload.similarQuestions?.length || 0} found</span>
              </div>
              {!Array.isArray(payload.similarQuestions) || payload.similarQuestions.length === 0 ? (
                <div className="p-8 text-center text-gray-600 text-sm">No similar questions found.</div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {payload.similarQuestions.map((question, index) => (
                    <div key={`${question.id}-${index}`} className="p-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div>
                        <p className="font-medium text-gray-900 line-clamp-2">{question.text || 'Untitled question'}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {question.subject || 'Unknown subject'} • {question.year || 'N/A'} • {question.section || 'N/A'}
                        </p>
                      </div>
                      <Link
                        to={`/external/questions/${question.id}/bot`}
                        className="inline-flex items-center justify-center bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-2 rounded-lg text-sm font-medium"
                      >
                        Ask Bot
                      </Link>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExternalQuestionBotPage;
